import React, { useState, useEffect } from "react";
import {
  X,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Undo,
  Redo,
  Image as ImageIcon,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  Loader2,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";

const BlogForm = ({ showModal, setShowModal, editingBlog, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    author: "",
    excerpt: "",
    image: "",
    content: "",
  });

  const [imagePreview, setImagePreview] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  // Initialize editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      Underline,
      Link,
      Image,
      BulletList,
      OrderedList,
    ],
    content: "", // Start with empty content
    editorProps: {
      attributes: {
        class:
          "prose prose-lg max-w-none p-6 bg-white min-h-[450px] rounded-xl shadow-sm border border-gray-200 focus:outline-none",
      },
    },
    onUpdate: ({ editor }) => {
      setFormData((prev) => ({
        ...prev,
        content: editor.getHTML(),
      }));
    },
  });

  // 🎯 FIXED: Populate editor content when editing or resetting
  useEffect(() => {
    if (!editor) return; // Wait for editor to be ready

    if (editingBlog) {
      // Editing mode - populate all fields
      const newData = {
        title: editingBlog.title || "",
        date: editingBlog.date || "",
        author: editingBlog.author || "",
        excerpt: editingBlog.excerpt || "",
        image: editingBlog.image || "",
        content: editingBlog.content || editingBlog.body || "", // Support both content and body
      };
      setFormData(newData);
      setImagePreview(editingBlog.image || "");
      
      // ✅ Set editor content - this is the key part
      editor.commands.setContent(newData.content);
    } else {
      // Add new mode - reset to defaults
      const newData = {
        title: "",
        date: new Date().toLocaleDateString("en-US", {
          weekday: "short",
          day: "2-digit",
          month: "long",
          year: "numeric",
        }),
        author: "SNSES NEWS",
        excerpt: "",
        image: "",
        content: "",
      };
      setFormData(newData);
      setImagePreview("");
      
      // ✅ Clear editor content
      editor.commands.clearContent();
    }
  }, [editingBlog, editor, showModal]); // Added showModal to refresh when modal opens

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setIsConverting(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      setTimeout(() => {
        const base64Image = e.target.result;
        setImagePreview(base64Image);
        setFormData((prev) => ({ ...prev, image: base64Image }));
        setIsConverting(false);
      }, 800);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageUpload(file);
  };

  const removeImage = () => {
    setImagePreview("");
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { title, author, image, content: body, excerpt } = formData;
    onSubmit({ title, author, image, body, excerpt });
    setShowModal(false);
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-5xl shadow-xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">
              {editingBlog ? "Edit Blog Post" : "Add New Blog Post"}
            </h2>
            <button
              onClick={() => setShowModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Blog Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter blog title"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-gray-800"
              />
            </div>

            {/* Date and Author */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <input
                  type="text"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author *
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Image *
              </label>

              {isConverting ? (
                <div className="flex items-center gap-2 text-amber-600">
                  <Loader2 className="animate-spin" /> Converting image...
                </div>
              ) : imagePreview ? (
                <div className="relative inline-block mb-4">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full max-w-md h-56 object-cover rounded-lg border border-gray-200 shadow"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div
                  className={`border-2 border-dashed rounded-xl p-6 text-center ${
                    dragOver
                      ? "border-amber-500 bg-amber-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                >
                  <ImageIcon size={48} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer text-amber-600 hover:text-amber-700"
                    >
                      Click to upload
                    </label>{" "}
                    or drag and drop
                  </p>
                  <p className="text-sm text-gray-500">
                    PNG, JPG, or GIF up to 10MB
                  </p>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0])}
                    className="hidden"
                  />
                </div>
              )}

              {/* OR use URL */}
              <div className="flex items-center my-4">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="px-3 text-sm text-gray-500">OR</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>

              <input
                type="url"
                placeholder="Enter image URL"
                value={formData.image}
                onChange={(e) => {
                  const url = e.target.value;
                  setImagePreview(url);
                  setFormData((prev) => ({ ...prev, image: url }));
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excerpt *
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                required
                rows={3}
                placeholder="Short summary of your blog post..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>

            {/* Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Blog Content *
              </label>

              {editor && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm mb-3 sticky top-0 z-10">
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`p-2 rounded ${
                      editor.isActive("bold") ? "bg-gray-300" : "hover:bg-gray-200"
                    }`}
                  >
                    <Bold size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded ${
                      editor.isActive("italic")
                        ? "bg-gray-300"
                        : "hover:bg-gray-200"
                    }`}
                  >
                    <Italic size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    className={`p-2 rounded ${
                      editor.isActive("underline")
                        ? "bg-gray-300"
                        : "hover:bg-gray-200"
                    }`}
                  >
                    <UnderlineIcon size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 1 }).run()
                    }
                    className={`p-2 rounded ${
                      editor.isActive("heading", { level: 1 })
                        ? "bg-gray-300"
                        : "hover:bg-gray-200"
                    }`}
                  >
                    <Heading1 size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      editor.chain().focus().toggleHeading({ level: 2 }).run()
                    }
                    className={`p-2 rounded ${
                      editor.isActive("heading", { level: 2 })
                        ? "bg-gray-300"
                        : "hover:bg-gray-200"
                    }`}
                  >
                    <Heading2 size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={`p-2 rounded ${
                      editor.isActive("bulletList")
                        ? "bg-gray-300"
                        : "hover:bg-gray-200"
                    }`}
                  >
                    <List size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={`p-2 rounded ${
                      editor.isActive("orderedList")
                        ? "bg-gray-300"
                        : "hover:bg-gray-200"
                    }`}
                  >
                    <ListOrdered size={18} />
                  </button>
                  <div className="w-px bg-gray-300 mx-2" />
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
                  >
                    <Undo size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    className="p-2 rounded hover:bg-gray-200 disabled:opacity-50"
                  >
                    <Redo size={18} />
                  </button>
                </div>
              )}

              <EditorContent editor={editor} />
            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition"
              >
                {editingBlog ? "Update Blog" : "Publish Blog"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlogForm;